import { createContext, useEffect, useState, useRef, useMemo, useCallback, Fragment, type ReactNode } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { doc, getDoc, onSnapshot, updateDoc, serverTimestamp, type DocumentSnapshot } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import type { OrgUser, OrgUserRole } from "@/types/firestore";

export interface AuthState {
  user: User | null;
  orgUser: OrgUser | null;
  role: OrgUserRole | null;
  loading: boolean;
  error: string | null;
}

export interface AuthContextValue extends AuthState {
  refreshUser: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

function validMillis(value: number): number | null {
  return Number.isFinite(value) ? value : null;
}

function firebaseSignInMillis(user: User): number | null {
  const value = user.metadata.lastSignInTime;
  if (!value) return null;
  return validMillis(Date.parse(value));
}

function storedLoginMillis(value: unknown): number | null {
  if (!value || typeof value !== "object" || !("toMillis" in value)) return null;
  const toMillis = value.toMillis;
  if (typeof toMillis !== "function") return null;
  try {
    return validMillis(toMillis.call(value));
  } catch {
    return null;
  }
}

function shouldStampLastLogin(user: User, storedValue: unknown): boolean {
  const signInMillis = firebaseSignInMillis(user);
  if (signInMillis === null) return false;
  const lastLoginMillis = storedLoginMillis(storedValue);
  return lastLoginMillis === null || signInMillis > lastLoginMillis;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    orgUser: null,
    role: null,
    loading: true,
    error: null,
  });
  const generation = useRef(0);
  const profileRead = useRef(0);
  const previousUid = useRef<string | null>(null);

  async function fetchOrgUser(user: User, supplied?: DocumentSnapshot, expectedGeneration = generation.current) {
    if (expectedGeneration !== generation.current) return;
    const read = ++profileRead.current;
    const stale = () => expectedGeneration !== generation.current || read !== profileRead.current;
    try {
      const ref = doc(db, "orgUsers", user.uid);
      const snap = supplied ?? await getDoc(ref);
      if (stale()) return;

      // No profile — all users must be invited first
      if (!snap.exists()) {
        setState({
          user,
          orgUser: null,
          role: null,
          loading: false,
          error: "No account found. Contact your administrator for an invitation.",
        });
        return;
      }

      const orgUser = snap.data() as OrgUser;

      const validRole = ["PLATFORM_OWNER", "PLATFORM_ADMIN", "ORG_OWNER", "ORG_ADMIN", "ORG_REQUESTER"].includes(orgUser.role);
      if (orgUser.status !== "active" || !validRole ||
          (orgUser.role === "ORG_REQUESTER" && (!orgUser.orgId || !user.emailVerified))) {
        setState({
          user,
          orgUser,
          role: null,
          loading: false,
          error: orgUser.status === "suspended" ? "Your account has been suspended." :
            orgUser.role === "ORG_REQUESTER" && !user.emailVerified ? "Verify your email before accessing support." : "This account does not have an active role.",
        });
        return;
      }

      // Check if org is suspended (for org-level users only)
      if (
        (orgUser.role === "ORG_OWNER" || orgUser.role === "ORG_ADMIN") &&
        orgUser.orgId
      ) {
        const orgSnap = await getDoc(doc(db, "orgs", orgUser.orgId));
        if (stale()) return;
        if (orgSnap.exists()) {
          const org = orgSnap.data();
          if (org.status === "suspended") {
            setState({
              user,
              orgUser,
              role: null,
              loading: false,
              error:
                "Your organization has been suspended. Please contact your BiG administrator.",
            });
            return;
          }
        }
      }

      if (shouldStampLastLogin(user, orgUser.lastLoginAt)) {
        await updateDoc(ref, { lastLoginAt: serverTimestamp() }).catch(() => {
          // Non-critical: profile access must not depend on the audit marker.
        });
      }

      if (stale()) return;

      setState({
        user,
        orgUser,
        role: orgUser.role,
        loading: false,
        error: null,
      });
    } catch (err) {
      if (stale()) return;
      setState({
        user,
        orgUser: null,
        role: null,
        loading: false,
        error: "Failed to load user profile.",
      });
      console.error("Error fetching orgUser:", err);
    }
  }

  const refreshUser = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      setState((prev) => ({ ...prev, loading: true }));
      await fetchOrgUser(currentUser);
    }
  }, []);

  useEffect(() => {
    let stopProfile: (() => void) | undefined;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      stopProfile?.();
      stopProfile = undefined;
      const currentGeneration = ++generation.current;
      if (previousUid.current !== null && previousUid.current !== user?.uid) sessionStorage.removeItem("impersonation");
      previousUid.current = user?.uid ?? null;
      setState({ user, orgUser: null, role: null, loading: !!user, error: null });
      if (user) {
        await fetchOrgUser(user, undefined, currentGeneration);
        if (currentGeneration !== generation.current) return;
        stopProfile = onSnapshot(doc(db, "orgUsers", user.uid),
          (snap) => void fetchOrgUser(user, snap, currentGeneration),
          () => {
            if (currentGeneration !== generation.current) return;
            profileRead.current++;
            setState({ user, orgUser: null, role: null, loading: false, error: "Account access is no longer available." });
          });
      } else {
        sessionStorage.removeItem("impersonation");
        setState({
          user: null,
          orgUser: null,
          role: null,
          loading: false,
          error: null,
        });
      }
    });

    // The counter invalidates async reads on teardown; it is not a DOM ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { generation.current++; stopProfile?.(); unsubscribe(); };
  }, []);

  const value = useMemo(
    () => ({ ...state, refreshUser }),
    [state, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      <Fragment key={`${state.user?.uid ?? "signed-out"}:${state.role}:${state.orgUser?.orgId}:${state.orgUser?.status}`}>
        {children}
      </Fragment>
    </AuthContext.Provider>
  );
}
